import { defineStore, storeToRefs } from 'pinia'
import { ref, toRaw, watch } from 'vue'
import { useDeviceStore, useLocaleStore } from '@/stores'
import { toast } from '@rapoo/element/components/ui/sonner'
import {
  type BaseDevice,
  Config,
  type ConfigCapability,
  DeviceError,
  ch585mDeviceCustom,
  type Role,
  RoleId,
  NoticeEvents,
  ReportEvents,
} from '@rapoo/device-sdk'
import { defaultReportError } from '@/composables/useErrorLogger'
import { hasConfigCapability } from '@/composables/useConfigName'
import { useLoading } from '@/composables/useLoading'

export interface GameApp {
  id: string
  name: string
  path?: string // 可执行文件路径
  icon?: string // 应用图标
  linkedProfileList?: Record<string, { index: number; type: string }> // 关联的配置列表，key为设备型号device.manifest.model，value为配置索引和设备类型
}

export const useConfigurationStore = defineStore('configuration', () => {
  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice, devices } = storeToRefs(deviceStore)

  // --- State ---

  // 配置列表
  const configs = ref<Config[]>([])

  // 当前激活的配置
  const currentConfig = ref<Config>()

  // 游戏应用列表
  const apps = ref<GameApp[]>([])

  // 加载状态
  const { isLoading, withLoading } = useLoading()

  // --- Actions ---

  // 获取所有配置
  const getAllConfigs = async () => {
    try {
      if (!currentDevice.value?.features.config) return
      const res = await currentDevice.value.features.config.getAllConfigs()
      if (res) {
        configs.value = res
      }
    } catch (err) {
      if (err instanceof DeviceError) {
        console.error(err.message)
      } else {
        console.error(err)
        toast.error(t('toast.configuration.getListFail'))
      }
      defaultReportError(`get all configs: ${JSON.stringify(err)}`)
      throw err
    }
  }

  // 获取当前配置
  const getCurrentConfig = async () => {
    try {
      if (!currentDevice.value?.features.config) return
      const config = await currentDevice.value.features.config.getCurrentConfig()
      if (config) {
        currentConfig.value = config
      }
    } catch (err) {
      if (err instanceof DeviceError) {
        console.error(err.message)
        toast.error(t('toast.configuration.getCurrentFail'))
      } else {
        console.error(err)
        toast.error(t('toast.configuration.getCurrentFail'))
      }
      defaultReportError(`get current config: ${JSON.stringify(err)}`)
      throw err
    }
  }

  const CFG_DIAG = '[CFG-DIAG]'

  /**
   * 用最新上报的 cfgSel 校正 currentConfig。
   * 进入配置页时 refreshProfiles 会再读一次设备；若读到过期缓存/硬件值，
   * 会把按键切换后已正确的 currentConfig 覆盖回去，这里用上报值兜底。
   */
  const reconcileCurrentConfigFromReport = () => {
    const device = currentDevice.value
    const cfgSel = (device?.userCustomData as { cfgSel?: number } | undefined)?.cfgSel
    if (cfgSel == null || currentConfig.value?.index === cfgSel) return
    const matched = configs.value.find((c) => c.index === cfgSel)
    console.info(CFG_DIAG, 'reconcile from report', {
      storeIndex: currentConfig.value?.index,
      deviceIndex: device?.currentConfig?.index,
      reportCfgSel: cfgSel,
      matched: matched?.index,
    })
    if (matched) {
      currentConfig.value = matched
    }
  }

  // 刷新配置 (从设备读取)
  const refreshProfiles = async (options?: { reconcileReport?: boolean }) => {
    const device = currentDevice.value
    const hasUserConfig = !!device?.features.config && hasConfigCapability(device)
    if (!hasUserConfig) {
      configs.value = []
      currentConfig.value = undefined
      return
    }
    if (isLoading.value) {
      console.warn(CFG_DIAG, 'refreshProfiles skipped while loading', {
        storeIndex: currentConfig.value?.index,
        deviceIndex: device?.currentConfig?.index,
      })
      return
    }

    try {
      isLoading.value = true
      await getAllConfigs()
      await getCurrentConfig()
      if (options?.reconcileReport !== false) {
        reconcileCurrentConfigFromReport()
      }
      console.info(CFG_DIAG, 'refreshProfiles done', {
        storeIndex: currentConfig.value?.index,
        deviceIndex: device?.currentConfig?.index,
        reportCfgSel: (device?.userCustomData as { cfgSel?: number } | undefined)?.cfgSel,
        configCount: configs.value.length,
      })
    } catch (error) {
      // 错误已在 getAllConfigs/getCurrentConfig 中处理
      defaultReportError(`refresh profiles: ${JSON.stringify(error)}`)
    } finally {
      isLoading.value = false
    }
  }

  // 添加配置，成功时返回新配置 index
  const addProfile = async (name: string, configIndex?: number): Promise<number | undefined> => {
    if (!currentDevice.value?.features.config) {
      toast.error(t('toast.configuration.notSupported'))
      return
    }
    return await withLoading(async () => {
      try {
        const index = await currentDevice.value?.features?.config?.createConfig(name, configIndex)
        toast.success(t('toast.configuration.createSuccess', { name }))
        await getAllConfigs()
        await getCurrentConfig()
        return index
      } catch (err) {
        if (err instanceof DeviceError) {
          if (err.code === 'WRITE_FAILED' || err.code === 'TIMEOUT') {
            toast.error(t('toast.configuration.createFail'))
          }
          if (err.code === 'MAX_LIMIT_REACHED') {
            toast.error(t('toast.configuration.maxLimitReached'))
          }
          console.error(err.message)
        } else {
          console.error(err)
          toast.error(t('toast.configuration.createFail'))
        }
        defaultReportError(`add profile: ${JSON.stringify(err)}`)
      }
    })
  }

  // 删除配置
  const removeProfile = async (index: number) => {
    if (!currentDevice.value?.features.config) return

    // 保护默认配置 (索引0) - 参考 KeyboardTesting.vue
    if (index === 0) {
      toast.warning('toast.configuration.defaultDeleteForbidden')
      return
    }

    const isCurrentProfile = currentConfig.value?.index === index
    const profileIndex = configs.value.findIndex((config) => config.index === index)
    const previousProfileIndex =
      profileIndex > 0 ? configs.value[profileIndex - 1]?.index : configs.value[0]?.index

    try {
      // 删除完一般会自行delay一个300ms左右的时间，所以这里要loading一下
      await withLoading(async () => {
        await currentDevice.value?.features?.config?.deleteConfig(index)
      })
      if (isCurrentProfile) {
        currentConfig.value = undefined
        // 产品要求删除当前配置后切换到列表中的上一个配置。
        await setActiveProfile(previousProfileIndex ?? 0)
      }
      await refreshProfiles()
      toast.success(t('toast.configuration.deleteSuccess'))
    } catch (err) {
      if (err instanceof DeviceError) {
        console.error(err.message)
      } else {
        console.error(err)
      }
      toast.error(t('toast.configuration.deleteFail'))
      defaultReportError(`remove profile: ${JSON.stringify(err)}`)
    }
  }

  // 更新配置名称
  const updateProfileName = async (config: Config, newName: string) => {
    if (!currentDevice.value?.features.config) return

    if (config.index === 0) {
      toast.warning(t('toast.configuration.defaultRenameForbidden'))
      return
    }

    try {
      await withLoading(async () => {
        await currentDevice.value?.features?.config?.renameConfig({
          ...config,
          name: newName,
        })
        toast.success(t('toast.configuration.renameSuccess'))

        await getAllConfigs()
        // 如果重命名的是当前配置，刷新当前配置状态
        if (currentConfig.value?.index === config.index) {
          await getCurrentConfig()
        }
      })
    } catch (err) {
      if (err instanceof DeviceError) {
        console.error(err.message)
        if (err.code === 'MAX_LIMIT_REACHED') {
          toast.error(t('toast.configuration.maxLimitName'))
        }
      } else {
        console.error(err)
        toast.error(t('toast.configuration.renameFail'))
      }
      defaultReportError(`update profile name: ${JSON.stringify(err)}`)
    }
  }

  // 设置当前激活配置
  const setActiveProfile = async (index: number) => {
    if (!currentDevice.value?.features.config) return
    const device = currentDevice.value
    console.info(CFG_DIAG, 'setActiveProfile start', {
      targetIndex: index,
      storeIndex: currentConfig.value?.index,
      deviceIndex: device.currentConfig?.index,
      reportCfgSel: (device.userCustomData as { cfgSel?: number } | undefined)?.cfgSel,
    })
    await withLoading(
      async () => {
        try {
          await device.features?.config?.setCurrentConfig(index)
          await getCurrentConfig()
          console.info(CFG_DIAG, 'setActiveProfile done', {
            targetIndex: index,
            storeIndex: currentConfig.value?.index,
            deviceIndex: device.currentConfig?.index,
            reportCfgSel: (device.userCustomData as { cfgSel?: number } | undefined)?.cfgSel,
          })
        } catch (err) {
          console.error(CFG_DIAG, 'setActiveProfile failed', {
            targetIndex: index,
            storeIndex: currentConfig.value?.index,
            deviceIndex: device.currentConfig?.index,
            err,
          })
          console.error(err)
          toast.error(t('toast.configuration.switchFail'))
          defaultReportError(`set active profile: ${JSON.stringify(err)}`)
        }
      },
      { delayMs: 10 },
    )
  }

  /**
   * 切换设备到指定角色可见的第一个配置
   * 传入绝对 slot id，由 SDK 内部根据当前模式处理 index 转换
   */
  const switchToRoleProfile = async (device: BaseDevice, role: 'admin' | 'user') => {
    const config = device.features.config
    if (!config) return
    const configCap = device.manifest?.capabilities.find(
      (item): item is ConfigCapability => item.type === 'config',
    )
    const isAdminRole = (roles: Role[]) =>
      roles.includes(RoleId.superAdmin) || roles.includes(RoleId.admin)
    const slots = configCap?.config.configSlots
    if (slots) {
      const matchedSlots = slots.filter((slot) =>
        role === 'admin' ? isAdminRole(slot.roles) : slot.roles.includes(RoleId.user),
      )
      const currentIndex = device.currentConfig?.index
      if (!matchedSlots.some((s) => s.id === currentIndex) && matchedSlots.length > 0) {
        await config.setCurrentConfig(matchedSlots[0].id)
      }
    }
  }

  // --- App Related Actions (保留但需适配新的 ID) ---

  // 初始化应用列表
  const initApps = () => {
    window.electronAPI?.getGameList().then((list) => {
      apps.value = list
    })
  }

  // 同步应用列表到本地
  const syncApps = async () => {
    window.electronAPI?.setGameList(toRaw(apps.value))
  }

  // 添加应用
  const addApp = (name: string, path: string, icon?: string) => {
    if (apps.value.some((app) => app.path === path)) return
    const newApp: GameApp = {
      id: Date.now().toString(),
      name,
      path,
      icon,
    }
    apps.value.push(newApp)
    syncApps()
  }

  // 删除应用
  const removeApp = (id: string) => {
    const index = apps.value.findIndex((app) => app.id === id)
    if (index !== -1) {
      apps.value.splice(index, 1)
      syncApps()
    }
  }

  // 链接配置到应用
  const linkProfileToApp = (appId: string, profileId: string) => {
    const app = apps.value.find((a) => a.id === appId)
    const model = currentDevice.value?.manifest.model
    if (app && model) {
      if (!app.linkedProfileList) {
        app.linkedProfileList = {}
      }
      app.linkedProfileList[model] = {
        index: Number(profileId),
        type: currentDevice.value?.manifest.type || 'unknown',
      }
      syncApps()
    }
  }

  // 取消配置与应用的链接
  const unlinkProfileFromApp = (appId: string, model: string) => {
    const app = apps.value.find((a) => a.id === appId)
    if (app && model) {
      delete app.linkedProfileList?.[model]
      syncApps()
    }
  }

  // 注册应用变更事件
  const registerAppEvents = () => {
    initApps()
    console.log('register app events')
    window.electronAPI?.onGameActivated(async (game) => {
      if (!game?.linkedProfileList) return

      const linkedEntries = Object.entries(game.linkedProfileList)
      if (linkedEntries.length === 0) return

      // 优先切换当前设备（获得即时视觉反馈），再切换其他关联设备
      const currentModel = currentDevice.value?.manifest.model
      const linkInfoMap = game.linkedProfileList as Record<string, { index: number; type: string }>
      const sortedEntries = [...Object.entries(linkInfoMap)].sort(([modelA], [modelB]) => {
        if (modelA === currentModel) return -1
        if (modelB === currentModel) return 1
        return 0
      })

      for (const [model, linkInfo] of sortedEntries) {
        const device = devices.value?.find((d) => d.original.manifest.model === model)?.original
        if (!device?.features?.config) continue

        try {
          if (device === currentDevice.value) {
            // 当前设备：用 store 的方法（含 loading 状态管理）
            if (currentConfig.value?.index === linkInfo.index) continue

            const targetConfig = configs.value.find((config) => config.index === linkInfo.index)
            if (targetConfig) {
              await setActiveProfile(linkInfo.index)
              device.emit(NoticeEvents.CfgChanged, targetConfig)
            }
          } else {
            // 非当前设备：直接通过 device API 切换配置
            await device.features.config.setCurrentConfig(linkInfo.index)
            // 获取该设备的配置列表以找到目标配置并通知
            const deviceConfigs = await device.features.config.getAllConfigs()
            const targetConfig = deviceConfigs?.find((c: Config) => c.index === linkInfo.index)
            if (targetConfig) {
              device.emit(NoticeEvents.CfgChanged, targetConfig)
            }
          }
        } catch (err) {
          console.error(`[Configuration] Failed to switch config for device ${model}:`, err)
        }
      }
    })
  }

  // --- Config Related Actions ---
  // 根据用户自定义配置刷新当前配置状态
  const refreshConfigs = async (userData: ch585mDeviceCustom) => {
    const prevStoreIndex = currentConfig.value?.index
    const deviceIndex = currentDevice.value?.currentConfig?.index
    if (userData.cfgSel != currentConfig.value?.index) {
      // 获取到上报去查网吧版更新1.9之后总是失败，这里先直接取历史数据
      // await getCurrentConfig()
      const matched = configs.value.find((c) => c.index === userData.cfgSel)
      console.info(CFG_DIAG, 'CfgChanged report', {
        reportCfgSel: userData.cfgSel,
        storeIndexBefore: prevStoreIndex,
        deviceIndex,
        matched: matched?.index,
        // 若 deviceIndex 仍是旧值，说明上报早于 device.currentConfig 更新（竞态诊断）
        deviceLagging:
          deviceIndex != null && userData.cfgSel != null && deviceIndex !== userData.cfgSel,
      })
      if (matched) {
        currentConfig.value = matched
      }
    }
  }
  // 注册硬件上报用户自定配置变更事件
  const registerConfigEvents = () => {
    if (currentDevice.value) {
      currentDevice.value.off(ReportEvents.CfgChanged, refreshConfigs)
      currentDevice.value.on(ReportEvents.CfgChanged, refreshConfigs)
    } else {
      const watcher = watch(currentDevice, (newVal) => {
        if (!newVal) return
        newVal.off(ReportEvents.CfgChanged, refreshConfigs)
        newVal.on(ReportEvents.CfgChanged, refreshConfigs)
        watcher()
      })
    }
  }

  // 监听设备变化，自动刷新配置
  watch(
    currentDevice,
    () => {
      refreshProfiles()
      registerConfigEvents()
    },
    { immediate: true },
  )

  return {
    configs,
    currentConfig,
    apps,
    isLoading,
    addProfile,
    removeProfile,
    updateProfileName,
    setActiveProfile,
    addApp,
    removeApp,
    linkProfileToApp,
    unlinkProfileFromApp,
    registerAppEvents,
    refreshProfiles,
    registerConfigEvents,
    switchToRoleProfile,
  }
})
